import React from 'react';
import { CircleDot, Trash2, RotateCcw } from 'lucide-react';
import Button from './Button';
import { formatDateTime } from '../../utils/datetime';
import './VersionsTable.css';

// DRAFT, PUBLISHED VERSIONS, AND THE ONE THAT IS LIVE.
//
// The same question is asked of a Universal API integration and of a pipeline template, and it has the
// same answer in both: what is being edited, what has been frozen, which frozen thing the world is
// pointed at. It was written once for the Universal API screen; the second caller made it shared
// rather than copied, because the part that gets done differently every time it is done twice is
// exactly this — whether "live" is a badge or a column, whether publishing also goes live, and whether
// deleting the live one is allowed.
//
// WHAT VARIES BETWEEN CALLERS IS ONLY WHAT A VERSION CONTAINS — files for an integration, a design for
// a template — so that is one column, named and measured by the caller. Everything else is the same
// statement about time and about what is running.
//
// PUBLISHING AND GOING LIVE ARE SEPARATE, and the hint says so, because that separation is the whole
// reason this exists: a version can be frozen and read before anything runs it, and a rollback is then
// a pointer move rather than an edit made under an outage.

/**
 * @param {Array}    versions        [{ id, version, published_at, published_by, notes }] newest first
 * @param {*}        liveVersionId   which one is live, or null
 * @param {string}   sizeLabel       the heading for the contents column ('Files', 'Routes', …)
 * @param {Function} sizeOf          (version) => what to show in it
 * @param {string}  [usageLabel]     a column for what is ON each version ('Implementations') — omitted, no column
 * @param {Function} [usageOf]       (version) => what to show in it
 * @param {object}  [draft]          { size } — the unfrozen working copy, shown as a final row
 * @param {Function} onMakeLive      (version) => void
 * @param {Function} onTakeOffline   () => void
 * @param {Function} [onDelete]      (version) => void — omitted, no delete is offered
 * @param {Function} [onRestore]     (version) => void — "start the draft again from this one"
 * @param {boolean} [busy]           every control disabled while something is in flight
 * @param {string}  [title]
 * @param {string}  [emptyLabel]     when nothing has been published yet
 */
const VersionsTable = ({
    versions = [],
    liveVersionId = null,
    sizeLabel = 'Files',
    sizeOf = (v) => v.file_count,
    usageLabel = null,
    usageOf = null,
    draft = null,
    onMakeLive,
    onTakeOffline,
    onDelete,
    onRestore,
    busy = false,
    title = 'Versions',
    emptyLabel = 'Nothing has been published yet.',
}) => {
    const isLive = (v) => String(v.id) === String(liveVersionId);
    // A version-0 row is how the Universal API screen carries its draft; a caller that keeps its draft
    // outside the table passes `draft` instead. Both end up as one row at the bottom, because to a
    // reader they are the same thing.
    const published = versions.filter(v => v.version !== 0);
    const draftRow = draft || versions.find(v => v.version === 0) || null;
    const usage = !!(usageLabel && usageOf);
    const columns = usage ? 7 : 6;

    return (
        <div className="vt">
            <div className="vt-head">
                <h2 className="vt-title">{title}</h2>
                <span className="vt-hint">Publishing freezes the draft. Going live is a separate step.</span>
            </div>
            <table className="vt-table">
                <thead>
                    <tr>
                        <th>Version</th><th>Published</th><th>By</th><th>{sizeLabel}</th>
                        {usage && <th>{usageLabel}</th>}<th>Notes</th>
                        <th aria-label="Actions" />
                    </tr>
                </thead>
                <tbody>
                    {published.map(v => (
                        <tr key={v.id}>
                            <td>
                                v{v.version}
                                {isLive(v) && <span className="vt-badge vt-badge-live"><CircleDot size={12} /> live</span>}
                            </td>
                            <td className="vt-muted">{v.published_at ? formatDateTime(v.published_at) : '—'}</td>
                            <td className="vt-muted">{v.published_by || '—'}</td>
                            <td>{sizeOf(v)}</td>
                            {usage && <td>{usageOf(v)}</td>}
                            <td className="vt-muted">{v.notes || '—'}</td>
                            <td className="vt-row-actions">
                                {isLive(v)
                                    ? (
                                        <Button variant="secondary" disabled={busy} onClick={onTakeOffline}>
                                            Take offline
                                        </Button>
                                    ) : (
                                        <Button variant="secondary" disabled={busy} onClick={() => onMakeLive(v)}>
                                            Make live
                                        </Button>
                                    )}
                                {/* START AGAIN FROM THIS ONE. Not the same as going live: this replaces
                                    what is being EDITED, and leaves what is running alone. */}
                                {onRestore && (
                                    <button type="button" className="vt-icon-btn" disabled={busy}
                                        title={`Replace the draft with v${v.version}`}
                                        aria-label={`Replace the draft with v${v.version}`}
                                        onClick={() => onRestore(v)}>
                                        <RotateCcw size={14} />
                                    </button>
                                )}
                                {/* THE LIVE ONE IS NOT DELETABLE — deleting it would also take the
                                    template offline, and that should be two deliberate acts rather than
                                    one click with a side effect. The server refuses it as well. */}
                                {onDelete && !isLive(v) && (
                                    <button type="button" className="vt-icon-btn vt-icon-danger" disabled={busy}
                                        title={`Delete v${v.version}`}
                                        aria-label={`Delete v${v.version}`}
                                        onClick={() => onDelete(v)}>
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}

                    {draftRow && (
                        <tr className="vt-draft">
                            <td><span className="vt-badge vt-badge-draft">draft</span></td>
                            <td className="vt-muted">—</td>
                            <td className="vt-muted">—</td>
                            <td>{draft ? draft.size : sizeOf(draftRow)}</td>
                            {usage && <td className="vt-muted">—</td>}
                            <td className="vt-muted">{(draftRow.notes) || 'not published'}</td>
                            <td />
                        </tr>
                    )}

                    {!published.length && !draftRow && (
                        <tr><td colSpan={columns} className="vt-muted">{emptyLabel}</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default VersionsTable;
